// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-CRX-89 — the lines of an administration protocol: each one is a catalog component OR free text, with
// volume (mL), timing and sig. Flushing lines carry volume + timing; premedication / kit lines usually do not,
// so those two fields are offered for flushing only.

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import AppButton from '../../../components/ui/AppButton'
import { AppTextField, ChoiceToggle, SelectField } from '../../../components/ui/FormFields'
import SearchableSelect from '../../../components/common/SearchableSelect'
import type { AdminProtocolComponent } from '../../../api/client'
import type { ProtocolCategory, ProtocolLineInput } from '../../../api/carerxProtocols'

const TIMINGS = [
  { value: '', label: '— timing —' }, { value: 'pre', label: 'Pre' }, { value: 'post', label: 'Post' },
  { value: 'lock', label: 'Lock' }, { value: 'de_access', label: 'De-access' },
]

export const emptyLine = (): ProtocolLineInput =>
  ({ component_id: null, line_text: '', volume_ml: null, timing: null, sig: null })

export default function ProtocolLinesEditor({ category, lines, components, onChange }: {
  category: ProtocolCategory
  lines: ProtocolLineInput[]
  components: AdminProtocolComponent[]
  onChange: (next: ProtocolLineInput[]) => void
}) {
  const catalog = components.filter(c => c.comp_type === category && c.active)
  const options = catalog.map(c => ({ value: c.id, label: c.name }))
  const isFlush = category === 'flushing'
  const set = (i: number, patch: Partial<ProtocolLineInput>) =>
    onChange(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)))

  return (
    <Stack spacing={1.5}>
      {lines.length === 0 && <Box sx={{ fontSize: 12.5, color: 'var(--color-text-soft)' }}>No lines yet.</Box>}
      {lines.map((l, i) => {
        const kind = l.component_id !== null ? 'component' : 'text'
        const comp = catalog.find(c => c.id === l.component_id)
        return (
          <Box key={i} data-testid={`protocol-line-${i}`}
            sx={{ border: '1px solid var(--color-border)', borderRadius: 2, p: 1.25 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Box sx={{ fontSize: 12, fontWeight: 700 }}>Line {i + 1}</Box>
              <ChoiceToggle ariaLabel={`Line ${i + 1} kind`} value={kind}
                options={[{ value: 'component', label: 'From catalog' }, { value: 'text', label: 'Free text' }]}
                onChange={v => set(i, v === 'component'
                  ? { component_id: catalog[0]?.id ?? '', line_text: null }
                  : { component_id: null, line_text: '' })} />
              <Box sx={{ flex: 1 }} />
              <AppButton size="small" color="error" aria-label={`Remove line ${i + 1}`}
                onClick={() => onChange(lines.filter((_, j) => j !== i))}>Remove</AppButton>
            </Stack>
            {kind === 'component' ? (
              <SearchableSelect ariaLabel={`Line ${i + 1} component`} value={l.component_id ?? ''}
                options={options} placeholder="Pick a component…" onChange={v => set(i, { component_id: v || '' })} />
            ) : (
              <AppTextField fullWidth size="small" label="Line text" value={l.line_text ?? ''}
                inputProps={{ maxLength: 500, 'aria-label': `Line ${i + 1} text` }}
                onChange={e => set(i, { line_text: e.target.value })} />
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              {isFlush && (
                <>
                  <AppTextField size="small" label="Volume (mL)" value={l.volume_ml ?? ''} sx={{ width: 130 }}
                    inputProps={{ maxLength: 40 }} onChange={e => set(i, { volume_ml: e.target.value || null })} />
                  <SelectField size="small" label="Timing" value={l.timing ?? ''} options={TIMINGS} sx={{ width: 140 }}
                    onChange={v => set(i, { timing: v || null })} />
                </>
              )}
              <AppTextField size="small" fullWidth label="Sig" value={l.sig ?? ''}
                placeholder={comp?.default_sig ?? ''} helperText={comp?.default_sig && !l.sig ? 'Blank = the catalog sig' : undefined}
                inputProps={{ maxLength: 2000, 'aria-label': `Line ${i + 1} sig` }}
                onChange={e => set(i, { sig: e.target.value || null })} />
            </Stack>
          </Box>
        )
      })}
      <Box>
        <AppButton size="small" variant="outlined" onClick={() => onChange([...lines, emptyLine()])}>+ Add line</AppButton>
      </Box>
    </Stack>
  )
}
